import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Blocks, AlertTriangle, Check } from 'lucide-react';
import * as modulosApi from '@/api/modulos.api';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Toggle } from '@/components/ui/Toggle';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';

/**
 * QUÉ SABE HACER ESTE NEGOCIO.
 *
 * Es el PASO 5 de `ALTA-CLIENTE.md`, el único que obligaba a abrir una consola
 * de base de datos y escribir a mano:
 *
 *     UPDATE workspaces SET "enabledModulesJson" = '[...]' WHERE slug = '...'
 *
 * La API (`PUT /api/modulos`) ya existía; lo que faltaba era esta pantalla.
 * Con ella, montar un cliente nuevo es de verdad «sin tocar código».
 *
 * ═══ LO QUE ESTA PANTALLA TIENE QUE DECIR EN VOZ ALTA ═══
 *
 * 1. Que apagar un módulo NO esconde un botón: esa capacidad deja de existir
 *    para el negocio, y su API responde 404. Es aislamiento, no cosmética.
 * 2. Que encender uno puede encender otro por dependencia — el servidor lo
 *    resuelve, y acá se dice cuál se agregó en vez de que aparezca solo.
 * 3. Cuáles están DECLARADOS pero no construidos. `payment_link` es el caso:
 *    encenderlo no rompe nada y tampoco hace nada, y venderlo como que
 *    funciona es lo que cuesta un cliente.
 */

export default function Modulos() {
  const toast = useToast();
  const qc = useQueryClient();

  const catalogo = useQuery({
    queryKey: ['modulos', 'catalogo'],
    queryFn: () => modulosApi.getCatalogo(),
  });
  const menu = useQuery({ queryKey: ['menu'], queryFn: () => modulosApi.getMenu() });

  const [encendidos, setEncendidos] = useState<Set<string>>(new Set());
  const [tocado, setTocado] = useState(false);

  // El estado real viene del servidor: las rutas activas dicen qué módulos
  // tiene el negocio. No se adivina desde una lista del panel.
  useEffect(() => {
    if (!menu.data) return;
    setEncendidos(new Set(menu.data.rutas.map((r) => r.moduloId)));
    setTocado(false);
  }, [menu.data]);

  const guardar = useMutation({
    mutationFn: () => modulosApi.setModulos([...encendidos]),
    onSuccess: (r) => {
      if (r.agregadosPorDependencia.length > 0) {
        toast.success(
          `Guardado. Se encendió también: ${r.agregadosPorDependencia.join(', ')} (hace falta).`,
        );
      } else {
        toast.success('Guardado');
      }
      setTocado(false);
      void qc.invalidateQueries({ queryKey: ['menu'] });
      void qc.invalidateQueries({ queryKey: ['modulos'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  const aplicarTipo = useMutation({
    mutationFn: (tipo: string) => modulosApi.aplicarTipo(tipo),
    onSuccess: (r) => {
      toast.success(`Se aplicó el paquete "${r.tipo}"`);
      void qc.invalidateQueries({ queryKey: ['menu'] });
      void qc.invalidateQueries({ queryKey: ['modulos'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo aplicar'),
  });

  if (catalogo.isLoading || menu.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (catalogo.isError || menu.isError) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="No se pudo cargar"
        description={
          catalogo.error instanceof Error ? catalogo.error.message : 'Error desconocido'
        }
        action={
          <Button
            onClick={() => {
              void catalogo.refetch();
              void menu.refetch();
            }}
          >
            Reintentar
          </Button>
        }
      />
    );
  }

  const modulos = catalogo.data?.modulos ?? [];
  const tipos = catalogo.data?.tipos ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Qué sabe hacer este negocio"
        description="Un módulo apagado no existe para este negocio: ni en el menú, ni en la API."
        action={
          <Button onClick={() => guardar.mutate()} disabled={!tocado || guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        }
      />

      {tipos.length > 0 && (
        <Card>
          <CardBody className="space-y-3">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">Arrancar con un paquete</h3>
            <p className="text-sm text-slate-500">
              Aplica de una vez lo que suele necesitar ese tipo de negocio.{' '}
              <strong>Reemplaza</strong> lo que esté encendido ahora, no lo suma.
            </p>
            <div className="flex flex-wrap gap-2">
              {tipos.map((t) => (
                <Button
                  key={t.tipo}
                  variant="outline"
                  size="sm"
                  disabled={aplicarTipo.isPending}
                  onClick={() => aplicarTipo.mutate(t.tipo)}
                >
                  {t.tipo}
                  <span className="ml-1 text-xs text-slate-400">({t.modulos.length})</span>
                </Button>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <div className="space-y-3">
        {modulos.map((m) => {
          const activo = encendidos.has(m.id);
          return (
            <Card key={m.id}>
              <CardBody className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-medium text-slate-900 dark:text-slate-100">{m.nombre}</h4>
                    {!m.construido && (
                      <Badge variant="warning" title="Declarado, pero todavía no hace nada">
                        próximamente
                      </Badge>
                    )}
                    {m.dependeDe.length > 0 && (
                      <span className="text-xs text-slate-400">
                        necesita: {m.dependeDe.join(', ')}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{m.descripcion}</p>
                  {!m.construido && activo && (
                    <p className="mt-1 text-xs text-amber-700">
                      Está encendido y no hace nada. No se lo vendas al cliente como que funciona.
                    </p>
                  )}
                </div>
                <Toggle
                  label={activo ? 'Encendido' : 'Apagado'}
                  checked={activo}
                  onChange={(v) => {
                    setEncendidos((prev) => {
                      const n = new Set(prev);
                      if (v) n.add(m.id);
                      else n.delete(m.id);
                      return n;
                    });
                    setTocado(true);
                  }}
                />
              </CardBody>
            </Card>
          );
        })}
      </div>

      {tocado && (
        <div className="sticky bottom-4 flex items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 shadow-sm">
          <p className="text-sm text-brand-900">
            Hay cambios sin guardar. Encender uno puede encender otro que haga falta.
          </p>
          <Button onClick={() => guardar.mutate()} disabled={guardar.isPending}>
            <Check className="h-4 w-4" />
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      )}

      <Card>
        <CardBody className="flex items-start gap-3">
          <Blocks className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
          <p className="text-sm text-slate-600">
            Apagar un módulo no esconde un botón: esa capacidad <strong>deja de existir</strong>{' '}
            para este negocio y su dirección responde «no encontrado». Por eso también sirve para
            separar de verdad a un cliente de otro.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
